import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  PlusIcon,
  PencilIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { loanService, LoanType } from '@/services/loanService'
import toast from 'react-hot-toast'

interface LoanTypeModalProps {
  loanType?: LoanType | null // null/undefined for create, LoanType for edit
  onClose: () => void
  onSuccess: () => void
}

interface LoanTypeFormData {
  name: string
  code: string
  description: string
  interestRate: string
  minAmount: string
  maxAmount: string
  maxTermMonths: string
  processingFee: string
  requiresGuarantor: boolean
  requiresCollateral: boolean
  isActive: boolean
}

const LoanTypeModal: React.FC<LoanTypeModalProps> = ({ 
  loanType, 
  onClose, 
  onSuccess 
}) => {
  const isEditing = !!loanType
  
  const [formData, setFormData] = useState<LoanTypeFormData>({
    name: loanType?.name || '',
    code: loanType?.code || '',
    description: loanType?.description || '',
    interestRate: loanType?.interestRate.toString() || '',
    minAmount: loanType?.minAmount.toString() || '',
    maxAmount: loanType?.maxAmount?.toString() || '',
    maxTermMonths: loanType?.maxTermMonths?.toString() || '',
    processingFee: loanType?.processingFee.toString() || '',
    requiresGuarantor: loanType?.requiresGuarantor || false,
    requiresCollateral: loanType?.requiresCollateral || false,
    isActive: loanType?.isActive ?? true
  })

  const queryClient = useQueryClient()

  // Create loan type mutation
  const createLoanTypeMutation = useMutation({
    mutationFn: async (data: Partial<LoanType>) => {
      return loanService.createLoanType(data)
    },
    onSuccess: () => {
      toast.success('Loan type created successfully')
      queryClient.invalidateQueries({ queryKey: ['loanTypes'] })
      queryClient.invalidateQueries({ queryKey: ['activeLoanTypes'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to create loan type')
    }
  })

  // Update loan type mutation
  const updateLoanTypeMutation = useMutation({
    mutationFn: async (data: Partial<LoanType>) => {
      return loanService.updateLoanType(loanType!.id, data)
    },
    onSuccess: () => {
      toast.success('Loan type updated successfully')
      queryClient.invalidateQueries({ queryKey: ['loanTypes'] })
      queryClient.invalidateQueries({ queryKey: ['activeLoanTypes'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to update loan type')
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // Basic validation
    if (!formData.name.trim()) {
      toast.error('Loan type name is required')
      return
    }

    if (!formData.code.trim()) {
      toast.error('Loan type code is required')
      return
    }

    if (!formData.interestRate || parseFloat(formData.interestRate) < 0) {
      toast.error('Please enter a valid interest rate')
      return
    }

    if (!formData.minAmount || parseFloat(formData.minAmount) <= 0) {
      toast.error('Please enter a valid minimum amount')
      return
    }

    if (!formData.processingFee || parseFloat(formData.processingFee) < 0) {
      toast.error('Please enter a valid processing fee')
      return
    }

    // Validate max amount if provided
    if (formData.maxAmount) {
      const maxAmount = parseFloat(formData.maxAmount)
      const minAmount = parseFloat(formData.minAmount)
      
      if (maxAmount <= 0) {
        toast.error('Maximum amount must be greater than 0')
        return
      }
      
      if (maxAmount <= minAmount) {
        toast.error('Maximum amount must be greater than minimum amount')
        return
      }
    }

    // Validate max term if provided
    if (formData.maxTermMonths) {
      const maxTerm = parseInt(formData.maxTermMonths)
      if (maxTerm <= 0) {
        toast.error('Maximum term must be greater than 0')
        return
      }
    }

    const loanTypeData = {
      name: formData.name.trim(),
      code: formData.code.trim(),
      ...(formData.description.trim() && { description: formData.description.trim() }),
      interestRate: parseFloat(formData.interestRate),
      minAmount: parseFloat(formData.minAmount),
      maxAmount: formData.maxAmount ? parseFloat(formData.maxAmount) : null,
      maxTermMonths: formData.maxTermMonths ? parseInt(formData.maxTermMonths) : null,
      processingFee: parseFloat(formData.processingFee),
      requiresGuarantor: formData.requiresGuarantor,
      requiresCollateral: formData.requiresCollateral,
      isActive: formData.isActive
    }

    if (isEditing) {
      updateLoanTypeMutation.mutate(loanTypeData)
    } else {
      createLoanTypeMutation.mutate(loanTypeData)
    }
  }

  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount)
    if (isNaN(num)) return ''
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(num)
  }

  const currentMutation = isEditing ? updateLoanTypeMutation : createLoanTypeMutation
  
  // Show warning if deactivating a loan type with existing data
  const showDeactivationWarning = isEditing && 
    loanType?.isActive && 
    !formData.isActive && 
    (loanType?._count?.loanApplications! > 0 || loanType?._count?.loans! > 0)

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between pb-3 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            {isEditing ? (
              <>
                <PencilIcon className="h-5 w-5 inline mr-2" />
                Edit Loan Type
              </>
            ) : (
              <>
                <PlusIcon className="h-5 w-5 inline mr-2" />
                Create Loan Type
              </>
            )}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mt-4">
          {/* Basic Information */}
          <div className="space-y-4 mb-6">
            <h4 className="text-md font-medium text-gray-900">Basic Information</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loan Type Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., Personal Loan, Car Loan, etc."
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loan Type Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., PERS_LOAN, CAR_LOAN"
                  required
                  disabled={isEditing} // Don't allow editing code for existing loan types
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="Brief description of this loan type..."
              />
            </div>
          </div>

          {/* Financial Terms */}
          <div className="space-y-4 mb-6">
            <h4 className="text-md font-medium text-gray-900">Financial Terms</h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interest Rate (%) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.interestRate}
                  onChange={(e) => setFormData({ ...formData, interestRate: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., 10.5"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Processing Fee (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.processingFee}
                  onChange={(e) => setFormData({ ...formData, processingFee: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., 50.00"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Amount (USD) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.minAmount}
                  onChange={(e) => setFormData({ ...formData, minAmount: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., 1000.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maximum Amount (USD)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.maxAmount}
                  onChange={(e) => setFormData({ ...formData, maxAmount: e.target.value })}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  placeholder="e.g., 100000.00 (optional)"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no maximum limit</p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Term (Months)
              </label>
              <input
                type="number"
                min="1"
                value={formData.maxTermMonths}
                onChange={(e) => setFormData({ ...formData, maxTermMonths: e.target.value })}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                placeholder="e.g., 60 (optional)"
              />
              <p className="text-xs text-gray-500 mt-1">Leave empty for no maximum term limit</p>
            </div>
          </div>

          {/* Requirements */}
          <div className="space-y-4 mb-6">
            <h4 className="text-md font-medium text-gray-900">Requirements</h4>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresGuarantor"
                  checked={formData.requiresGuarantor}
                  onChange={(e) => setFormData({ ...formData, requiresGuarantor: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="requiresGuarantor" className="ml-2 block text-sm text-gray-900">
                  Requires Guarantor
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="requiresCollateral"
                  checked={formData.requiresCollateral}
                  onChange={(e) => setFormData({ ...formData, requiresCollateral: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="requiresCollateral" className="ml-2 block text-sm text-gray-900">
                  Requires Collateral
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                  Active (Available for new applications)
                </label>
              </div>
            </div>
          </div>

          {/* Preview */}
          {formData.minAmount && formData.interestRate && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-blue-900 mb-1">Preview:</div>
                  <div className="text-blue-800 space-y-1">
                    <div>Amount Range: {formatCurrency(formData.minAmount)} - {formData.maxAmount ? formatCurrency(formData.maxAmount) : 'No limit'}</div>
                    <div>Interest Rate: {formData.interestRate}% per year</div>
                    <div>Processing Fee: {formatCurrency(formData.processingFee)}</div>
                    {formData.maxTermMonths && <div>Maximum Term: {formData.maxTermMonths} months</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Deactivation Warning */}
          {showDeactivationWarning && (
            <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-yellow-900 mb-1">Warning:</div>
                  <div className="text-yellow-800">
                    This loan type has {loanType?._count?.loanApplications} applications and {loanType?._count?.loans} active loans. 
                    Deactivating it will prevent new applications but won't affect existing ones.
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={currentMutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {currentMutation.isPending 
                ? (isEditing ? 'Updating...' : 'Creating...') 
                : (isEditing ? 'Update Loan Type' : 'Create Loan Type')
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoanTypeModal